import React, { FormEvent } from 'react'
import { useForm, Link } from '@inertiajs/react'
import * as yup from 'yup'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { AppLayout } from '@/components/layouts/AppLayout'

interface FolderOption {
  id: number
  name: string
}

interface Props {
  notebook: { id: number; name: string; slug: string }
  folders: FolderOption[]
}

const noteSchema = yup.object({
  note: yup.object({
    title: yup.string().required('Title is required').max(255, 'Title must be at most 255 characters'),
  })
})

export default function New({ notebook, folders }: Props) {
  const { data, setData, post, processing, errors, setError, clearErrors } = useForm({
    note: {
      title: '',
      content: '',
      folder_id: '' as number | '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    try {
      noteSchema.validateSync(data, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    post(`/notebooks/notebooks/${notebook.slug}/notes`)
  }

  return (
    <AppLayout title="New note" maxWidth="md">
      <Box sx={{ mb: 2 }}>
        <Button
          component={Link}
          href={`/notebooks/notebooks/${notebook.slug}`}
          size="small"
        >
          Back to {notebook.name}
        </Button>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Title"
            fullWidth
            autoFocus
            value={data.note.title}
            onChange={e => setData('note', { ...data.note, title: e.target.value })}
            error={'note.title' in errors}
            helperText={(errors as Record<string, string>)['note.title']}
            sx={{ mb: 3 }}
          />
          {folders.length > 0 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Folder (optional)</InputLabel>
              <Select
                value={data.note.folder_id}
                label="Folder (optional)"
                onChange={e => setData('note', { ...data.note, folder_id: e.target.value as number | '' })}
              >
                <MenuItem value="">No folder</MenuItem>
                {folders.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Content (Markdown)"
            fullWidth
            multiline
            rows={16}
            value={data.note.content}
            onChange={e => setData('note', { ...data.note, content: e.target.value })}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
          >
            Create note
          </Button>
        </Box>
      </Paper>
    </AppLayout>
  )
}
